import React, { useState, useEffect } from 'react';

import { currentAgentService } from '../../../services/currentAgentService';
import Icon from '../../../components/AppIcon';

const WelcomeMessage = ({ onSendMessage, onStartBailProcess }) => {
  const [currentAgentInitials, setCurrentAgentInitials] = useState('DS');
  const [currentAgentName, setCurrentAgentName] = useState('Damian Sailors');

  // ✅ NEW: Load current agent info
  useEffect(() => {
    const loadAgentInfo = async () => {
      try {
        const initials = await currentAgentService?.getCurrentAgentInitials();
        const name = await currentAgentService?.getCurrentAgentName();
        setCurrentAgentInitials(initials);
        setCurrentAgentName(name);
      } catch (error) {
        console.log('Error loading agent info:', error);
        // Keep fallback values
      }
    };

    loadAgentInfo();

    // Listen for agent changes
    const handleAgentChange = () => {
      loadAgentInfo();
    };

    window.addEventListener('currentAgentChanged', handleAgentChange);
    return () => window.removeEventListener('currentAgentChanged', handleAgentChange);
  }, []);

  const handleQuickAction = (message) => {
    if (onSendMessage) {
      onSendMessage(message);
    }
  };

  const quickActions = [
    {
      icon: 'Search',
      label: 'Search for someone',
      action: 'search John Doe',
      bg: 'from-blue-600 to-blue-700',
      description: 'Find an arrestee in Sacramento County jails'
    },
    {
      icon: 'DollarSign',
      label: 'Start Bail Process',
      action: 'START_BAIL_FORM',
      bg: 'from-red-600 to-red-700',
      description: 'Begin emergency bail assistance immediately'
    },
    {
      icon: 'MapPin',
      label: 'Jail locations',
      action: 'What jails do you cover?',
      bg: 'from-purple-600 to-purple-700',
      description: 'Find facility addresses and visiting hours'
    },
    {
      icon: 'Phone',
      label: 'Emergency help',
      action: 'I need urgent help',
      bg: 'from-orange-600 to-orange-700',
      description: '24/7 immediate assistance available'
    }
  ];

  const handleQuickActionClick = (action) => {
    if (action === 'START_BAIL_FORM') {
      // Call the parent handler to show bail form
      if (onStartBailProcess) {
        onStartBailProcess();
      }
    } else {
      // Send as regular message
      if (onSendMessage) {
        onSendMessage(action);
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[500px] px-8 py-12">
      <div className="max-w-4xl w-full text-center">
        {/* Main Agent Avatar and Introduction */}
        <div className="flex flex-col items-center mb-12">
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
              <span className="text-white text-2xl font-bold">{currentAgentInitials}</span>
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full border-3 border-white flex items-center justify-center">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
          
          <div className="space-y-3">
            <h1 className="text-4xl font-bold text-slate-800">
              Hello! I'm <span className="text-blue-600">{currentAgentName}</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Licensed California Bail Agent ready to help you 24/7. I have immediate access to Sacramento County, Placer County, El Dorado County, San Joaquin, Yuba County, Yolo County, and Stanislaus County jail databases and can start the bail process right now.
            </p>
          </div>
        </div>
        
        {/* Streamlined Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {quickActions?.map((action, index) => (
            <div
              key={index}
              onClick={() => handleQuickActionClick(action?.action)}
              className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border border-slate-200 hover:border-slate-300 transform hover:scale-105"
            >
              <div className="flex items-center space-x-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${action?.bg} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                  <Icon name={action?.icon} size={24} color="white" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">
                    {action?.label}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {action?.description}
                  </p>
                </div>
                <Icon 
                  name="ChevronRight" 
                  size={20} 
                  className="text-slate-400 group-hover:text-slate-600 transition-colors duration-200" 
                />
              </div>
            </div>
          ))}
        </div>
        
        {/* Professional Support Message */}
        <div className="mt-8 text-center">
          <div className="max-w-2xl mx-auto bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl p-6 shadow-sm">
            <p className="text-slate-700 italic leading-relaxed">
              "I understand this situation can be overwhelming. I'm here to guide you through every step 
              with clear communication and professional support. No judgment—just results."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;